import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import { ThirdPartyPaymentAuthorizationDtoIn } from '@/models/in/ThirdPartyPaymentAuthorizationDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import useTableDensity from '@/hooks/useTableDensity';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import EditOrCreateAuthorizationDialog from './EditOrCreateAuthorizationDialog';
import DeleteAuthorizationDialog from './DeleteAuthorizationDialog';

const QUERY_URL = 'management/third-party-payment-authorizations';
const DENSITY_STORAGE_KEY = 'table.density.thirdPartyAuthorizations';

/**
 * Who may have support money paid to someone other than the beneficiary (HAT-1259).
 *
 * Rows outside their validity window, and refusals, are shown and marked rather than hidden -
 * the office asked for the same treatment the funds board gives overdraft frames, so that every
 * decision it has recorded stays visible.
 */
const ThirdPartyPaymentAuthorizationsManagementPage = () => {
  const { t } = useTranslation();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [authorizationToEdit, setAuthorizationToEdit] = useState<ThirdPartyPaymentAuthorizationDtoIn | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [authorizationToDelete, setAuthorizationToDelete] = useState<ThirdPartyPaymentAuthorizationDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  const { data: authorizations, isLoading, refetch } = useGetAuthApi<ThirdPartyPaymentAuthorizationDtoIn[]>(QUERY_URL);

  const handleOpenCreateDialog = useCallback(() => {
    setAuthorizationToEdit(null);
    setIsEditDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((authorization: ThirdPartyPaymentAuthorizationDtoIn) => {
    setAuthorizationToEdit(authorization);
    setIsEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setAuthorizationToEdit(null);
  }, []);

  const handleOpenDeleteDialog = useCallback((authorization: ThirdPartyPaymentAuthorizationDtoIn) => {
    setAuthorizationToDelete(authorization);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setAuthorizationToDelete(null);
  }, []);

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('he-IL');

  const columns: ColumnDef<ThirdPartyPaymentAuthorizationDtoIn>[] = [
    {
      id: 'scope',
      header: Col(t('thirdPartyAuthorizations.scope')),
      accessorFn: (row) => row.contactId
        ? t('thirdPartyAuthorizations.beneficiaryScope')
        : t('thirdPartyAuthorizations.fundScope'),
      enableSorting: true,
      meta: { className: 'min-w-[110px] max-w-[110px]' },
    },
    {
      id: 'fundNumber',
      header: Col(t('thirdPartyAuthorizations.fundNumber')),
      accessorFn: (row) => row.fundId ?? '',
      enableSorting: true,
      meta: { className: 'min-w-[110px] max-w-[110px]' },
    },
    {
      id: 'name',
      header: Col(t('thirdPartyAuthorizations.name')),
      accessorFn: (row) => row.contactName ?? row.fundName ?? '',
      enableSorting: true,
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'status',
      header: Col(t('thirdPartyAuthorizations.status')),
      accessorFn: (row) => row.status,
      cell: ({ row }) => {
        const { status, isCurrentlyActive } = row.original;
        if (status === 'Rejected') {
          return <span className="text-red-500">{t('thirdPartyAuthorizations.rejected')}</span>;
        }
        return isCurrentlyActive
          ? <span className="text-green-600">{t('thirdPartyAuthorizations.active')}</span>
          // Approved but outside its dates. Called "not in effect" rather than "expired" because
          // a window that has not started yet lands here too.
          : <span className="text-(--font-secondary)">{t('thirdPartyAuthorizations.notInEffect')}</span>;
      },
      enableSorting: true,
      meta: { className: 'min-w-[120px] max-w-[120px]' },
    },
    {
      id: 'maxAmount',
      header: Col(t('thirdPartyAuthorizations.maxAmount')),
      accessorKey: 'maxAmount',
      cell: ({ row }) => {
        const { maxAmount, limitScope } = row.original;
        if (!maxAmount) return <span>{t('thirdPartyAuthorizations.noCeiling')}</span>;
        return (
          <div className="flex flex-col">
            <span>{maxAmount.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' })}</span>
            <span className="body-sm-regular text-(--font-secondary)">
              {t(`thirdPartyAuthorizations.limitScope.${limitScope}`)}
            </span>
          </div>
        );
      },
      enableSorting: true,
      meta: { className: 'min-w-[160px] max-w-[160px]' },
    },
    {
      id: 'validity',
      header: Col(t('thirdPartyAuthorizations.validity')),
      accessorFn: (row) => row.endDate,
      // Two lines, not "start – end" on one: with this many columns the table squeezes every one of
      // them to fit, so a one-line range was clipped from the left and the start date - the half
      // that says when the authorization begins - was the part that disappeared. Widening the
      // column did not help (the squeeze is proportional); stacking needs no width at all.
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{t('thirdPartyAuthorizations.validFromShort', { date: formatDate(row.original.startDate) })}</span>
          <span>{t('thirdPartyAuthorizations.validUntilShort', { date: formatDate(row.original.endDate) })}</span>
        </div>
      ),
      enableSorting: true,
      meta: { className: 'min-w-[120px] max-w-[120px]' },
    },
    {
      id: 'payeeIsraeliId',
      header: Col(t('thirdPartyAuthorizations.payeeIsraeliId')),
      accessorFn: (row) => row.payeeIsraeliId ?? '',
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return <span dir="ltr">{value || t('thirdPartyAuthorizations.anyPayee')}</span>;
      },
      enableSorting: false,
      meta: { className: 'min-w-[130px] max-w-[130px]' },
    },
    {
      id: 'decisionReason',
      header: Col(t('thirdPartyAuthorizations.decisionReason')),
      accessorKey: 'decisionReason',
      enableSorting: false,
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'decidedBy',
      header: Col(t('thirdPartyAuthorizations.decidedBy')),
      accessorFn: (row) => row.updatedBy ?? row.createdBy ?? '',
      enableSorting: true,
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'documents',
      header: Col(t('thirdPartyAuthorizations.documents')),
      cell: ({ row }) => {
        const docs = row.original.documents;
        if (!docs || docs.length === 0) return <span>-</span>;
        return (
          <div className="flex flex-col gap-1">
            {docs.map((doc, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <a
                  href={doc.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="body-sm-regular text-blue-600 hover:text-blue-800 underline truncate"
                  onClick={(e) => e.stopPropagation()}
                >
                  {doc.name}
                </a>
                <DownloadFileButton uri={doc.uri} fileName={doc.name} />
              </div>
            ))}
          </div>
        );
      },
      enableSorting: false,
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'actions',
      header: Col(t('thirdPartyAuthorizations.actions')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditDialog(row.original);
            }}
          >
            {t('thirdPartyAuthorizations.edit')}
          </Button>
          <Button
            variant="outline"
            className="!text-red-500 !border-red-300"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteDialog(row.original);
            }}
          >
            {t('thirdPartyAuthorizations.delete')}
          </Button>
        </div>
      ),
      meta: { className: 'min-w-[200px]', lockLayout: true },
    },
  ];

  return (
    <div>
      <DataTable
        title={t('thirdPartyAuthorizations.title')}
        data={authorizations ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <Button variant="primary" onClick={handleOpenCreateDialog}>
            {t('thirdPartyAuthorizations.addAuthorization')}
          </Button>
        }
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="thirdPartyAuthorizations"
        searchPlaceholder="thirdPartyAuthorizations.search"
        customGlobalSearch={() => true}
      />

      <EditOrCreateAuthorizationDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        authorizationToEdit={authorizationToEdit}
        onSuccess={refetch}
      />

      {isDeleteDialogOpen && authorizationToDelete && (
        <DeleteAuthorizationDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          authorization={authorizationToDelete}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default ThirdPartyPaymentAuthorizationsManagementPage;
